import _ from 'underscore';
import { getAgeGenderColumnConfig } from './ageGender-grid-column-config';
import { Constants } from '../constants';
import { columnConfigToColumnChooserConfig } from '../column-config-to-chooser-config';

export function getAgeGenderGridConfig({
  pageContext,
  i18n,
  jsDataCampaign,
  edm,
  ageSchema,
  getCurrentLevel,
  deleteSchema,
  bingadsGridColumn,
  gridType,
  isForVNext,
}) {
  const {
    IsAvgPositionDeprecationPhase1Enabled,
  } = pageContext.Permissions;
  const ageColumnConfig = getAgeGenderColumnConfig({ i18n, bingadsGridColumn, pageContext });
  const level = getCurrentLevel();
  return {
    getColumns: () => ageColumnConfig.getGridSpecificColumns()
      .concat(ageColumnConfig.getLevelOfDetailColumns(level))
      .concat(ageColumnConfig.getPerfColumns())
      .concat(ageColumnConfig.getConversionColumns()),
    getColumnChooserConfiguration: () => {
      const lockedColumns = ['AgeGroup', 'Gender', 'EstimatedImpressions'];
      const columnChooserConfig = {
        categories: [{
          name: i18n.getString('LevelOfDetail'),
          options: columnConfigToColumnChooserConfig(
            [
              ...ageColumnConfig.getGridSpecificColumns(),
              ...ageColumnConfig.getLevelOfDetailColumns(level),
            ],
            lockedColumns
          ),
        }, {
          name: i18n.getString('Performance'),
          options: columnConfigToColumnChooserConfig(
            ageColumnConfig.getPerfColumns(),
            lockedColumns
          ),
        }, {
          name: i18n.getString('Conversions'),
          options: columnConfigToColumnChooserConfig(
            ageColumnConfig.getConversionColumns(),
            lockedColumns
          ),
        }],
        selected: lockedColumns.concat(_.compact([
          'EstimatedImpressions',
          'EstimatedClicks',
          'EstimatedCTR',
          IsAvgPositionDeprecationPhase1Enabled && 'TopImpressionRatePercent',
          IsAvgPositionDeprecationPhase1Enabled && 'AbsoluteTopImpressionRatePercent',
        ])),
        locked: lockedColumns,
        mandatory: [],
        patchQuery: true,
        isForVNext,
      };

      if (isForVNext) {
        columnChooserConfig.customToolbarIcon = 'ba-TripleColumnSolid';
      }

      return columnChooserConfig;
    },

    getEntity: () => jsDataCampaign.DimensionAgeGender,

    getEntitySet: () => _.result(edm.currentAccount, 'DimensionAgeGender'),

    getSchema: () => {
      const columns = ['CampaignName', 'AdGroupName'];

      deleteSchema(columns, ageSchema);

      if (level === Constants.AccountLevel) {
        ageSchema.properties.CampaignName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
        ageSchema.properties.AdGroupName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
      } else if (level === Constants.CampaignLevel) {
        ageSchema.properties.AdGroupName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
      }

      return ageSchema;
    },
    getReportName: () => 'AgeAndGender ',
    getReportSchema() {
      return _.defaults({
        name: this.getReportName(gridType),
      }, this.getSchema(gridType));
    },
    modifyFilter: (filters) => {
      const ageGroup = filters.fields.filter(item => item.name === 'AgeGroup')[0];

      ageGroup.chooseType = 'checkbox';
      ageGroup.enum = [0, 1, 2, 3, 4, 5, 6, 7];
      ageGroup.localized = {
        0: i18n.getString('Dimension_AgeGroup_0'),
        1: i18n.getString('Dimension_AgeGroup_1'),
        2: i18n.getString('Dimension_AgeGroup_2'),
        3: i18n.getString('Dimension_AgeGroup_3'),
        4: i18n.getString('Dimension_AgeGroup_4'),
        5: i18n.getString('Dimension_AgeGroup_5'),
        6: i18n.getString('Dimension_AgeGroup_6'),
        7: i18n.getString('Dimension_AgeGroup_7'),
      };
      ageGroup.type = 'object';

      const gender = filters.fields.filter(item => item.name === 'Gender')[0];

      gender.chooseType = 'checkbox';
      gender.enum = [0, 1, 2];
      gender.localized = {
        0: i18n.getString('Dimension_Gender_0'),
        1: i18n.getString('Dimension_Gender_1'),
        2: i18n.getString('Dimension_Gender_2'),
      };
      gender.type = 'object';

      const medium = filters.fields.filter(item => item.name === 'Medium')[0];

      medium.chooseType = 'checkbox';
      medium.enum = [0, 1, 2, 10, 11];
      medium.localized = {
        0: i18n.getString('Dimension_Medium_0'),
        1: i18n.getString('Dimension_Medium_1'),
        2: i18n.getString('Dimension_Medium_2'),
        10: i18n.getString('Dimension_Medium_3'),
        11: i18n.getString('Dimension_Medium_4'),
      };
      medium.type = 'object';

      return filters;
    },
    getRewriteMap: () => ({
      AgeGroup: i18n.getString('ColumnName_AgeGroup'),
      Gender: i18n.getString('ColumnName_Gender'),
      Medium: i18n.getString('ColumnName_Medium'),
      EstimatedImpressions: i18n.getString('ColumnName_EstimatedImpressions'),
      EstimatedClicks: i18n.getString('ColumnName_EstimatedClicks'),
      EstimatedCTR: i18n.getString('ColumnName_EstimatedCTR'),
      EstimatedImpressionRate: i18n.getString('ColumnName_EstimatedImpressionRate'),
      EstimatedClickRate: i18n.getString('ColumnName_EstimatedClickPercent'),
      EstimatedConversions: i18n.getString('ColumnName_EstimatedConversions'),
      EstimatedConversionRate: i18n.getString('ColumnName_EstimatedConversionRate'),
      EstimatedAllConversions: i18n.getString('ColumnName_EstimatedAllConversions'),
      EstimatedAllConversionRate: i18n.getString('ColumnName_EstimatedAllConversionRate'),
      AdGroupName: i18n.getString('FilterLocalization_DataAttribute_AdGroupName'),
      CampaignName: i18n.getString('FilterLocalization_DataAttribute_CampaignName'),
      TopImpressionRatePercent: i18n.getString('ColumnName_TopImpressionRate'),
      AbsoluteTopImpressionRatePercent: i18n.getString('ColumnName_AbsoluteTopImpressionRate'),
    }),
  };
}
